import {
  ContentContainer,
  EllipsedText,
  FlexRow,
  MaterialIcon,
  StyledSearchBar,
  useDebouncedSearch,
  useLoading,
  useModal,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useState } from 'react';
import { COMPANIES_TABLE_COLUMNS_NAME } from '../../constants/companies';
import { useStyles } from '../../hooks/useStyles';
import { getCompanies } from '../../services/companiesService';
import { Company } from '../../types/companies';
import { CompanyModal } from '../companies/CompanyModal';
import { TableWrapper } from '../shared/TableWrapper';
import StyledTooltip from '../ui/StyledTooltip';

export function CompanyListPage() {
  const { css, theme, classes } = useStyles();
  const [companies, setCompanies] = useState<Company[]>();
  const [selectedCompany, setSelectedCompany] = useState<Company>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState<number | undefined>(undefined);
  const [totalItems, setTotalItems] = useState(0);
  const { search, setSearch } = useDebouncedSearch(useState<string>());
  const { openModal, closeModal, showModal } = useModal();

  useEffect(() => {
    setPage(0);
  }, [search]);

  useEffect(() => {
    if (page === undefined) return;
    if (page === 0) {
      setPage(1);
      return;
    }
    startLoading();
    const loadCompanies = async () => {
      const { paginatedResponse, error } = await getCompanies(page, search);
      if (!error && paginatedResponse) {
        setCompanies(paginatedResponse.items);
        setTotalItems(paginatedResponse.total);
      } else {
        setCompanies(undefined);
        setTotalItems(0);
      }
      stopLoading();
    };
    loadCompanies();
  }, [page]);

  const onUpdate = () => {
    setPage(0);
  };

  const onOpenModal = (company?: Company) => {
    setSelectedCompany(company);
    openModal();
  };

  const onSubmitFinished = () => {
    onUpdate();
    onCloseModal();
  };

  const onCloseModal = () => {
    setSelectedCompany(undefined);
    closeModal();
  };

  return (
    <ContentContainer>
      <FlexRow gap={theme.sizing.scale300}>
        <div className={classes.flexible(1)}>
          <StyledSearchBar
            name="company-search"
            value={search}
            onChange={setSearch}
            clearable
            noMargin
          />
        </div>
        <FlexRow
          gap={0}
          classNames={css({ alignItems: 'center' })}
        >
          <StyledTooltip
            content={'Nueva'}
            showArrow={false}
          >
            <Button
              size="mini"
              shape="square"
              onClick={() => onOpenModal()}
              kind="tertiary"
            >
              <MaterialIcon name={'add'} />
            </Button>
          </StyledTooltip>
          <StyledTooltip
            content={'Actualizar'}
            showArrow={false}
          >
            <Button
              size="mini"
              shape="square"
              onClick={onUpdate}
              kind="tertiary"
            >
              <MaterialIcon name={'sync'} />
            </Button>
          </StyledTooltip>
        </FlexRow>
      </FlexRow>
      <TableWrapper
        columns={COMPANIES_TABLE_COLUMNS_NAME}
        data={companies?.map((company, i) => {
          return {
            items: [
              <EllipsedText key={`company-name-${i}`}>{company.name}</EllipsedText>,
              <EllipsedText key={`company-social-${i}`}>{company.socialReason ?? '-'}</EllipsedText>,
              <EllipsedText key={`company-status-${i}`}>{company.active ? 'Si' : 'No'}</EllipsedText>,
              <FlexRow key={`company-actions-${i}`}>
                <StyledTooltip
                  content={'Nuevo'}
                  showArrow={false}
                >
                  <Button
                    size="mini"
                    shape="square"
                    onClick={() => onOpenModal(company)}
                    kind="tertiary"
                  >
                    <MaterialIcon
                      name={'edit'}
                      size="xs-mini"
                    />
                  </Button>
                </StyledTooltip>
              </FlexRow>,
            ],
          };
        })}
        hasData={companies !== undefined}
        isEmpty={!companies?.length}
        loading={loading}
        noDataDescription="Aun no tienes ninguna compañia"
        onPageChange={setPage}
        page={page ?? 0}
        totalItems={totalItems}
        hasSearch
      />
      <CompanyModal
        isOpen={showModal}
        onClose={onCloseModal}
        onSubmitFinished={onSubmitFinished}
        company={selectedCompany}
        companyId={selectedCompany?._id}
      />
    </ContentContainer>
  );
}
